/** Semantic headings — allowed on **heading** elements (and optional typography on **text**). */
export const HEADING_HTML_TAGS = ["h1", "h2", "h3", "h4", "h5", "h6"] as const;

export type HeadingHtmlTag = (typeof HEADING_HTML_TAGS)[number];

/** Flow / supporting tags for rich typography (no headings). */
export const TEXT_FLOW_HTML_TAGS = [
	"p",
	"span",
	"div",
	"blockquote",
	"cite",
	"label",
	"figcaption",
	"small",
	"address",
	/** Row inside a `ul`/`ol` container; keeps real list semantics for CSS and a11y. */
	"li",
	/** Clickable label of a `details` container. */
	"summary",
] as const;

/** Full tag palette for Content → Tag on both **text** and **heading** blocks. */
export const TYPOGRAPHY_HTML_TAGS = [
	...HEADING_HTML_TAGS,
	...TEXT_FLOW_HTML_TAGS,
] as const;

export type TypographyHtmlTag = (typeof TYPOGRAPHY_HTML_TAGS)[number];

/** @deprecated Use {@link TypographyHtmlTag}; text blocks may use headings too. */
export type TextHtmlTag = TypographyHtmlTag;

/** Allowed tags for inspector lists (same as {@link TYPOGRAPHY_HTML_TAGS}). */
export const TEXT_ELEMENT_HTML_TAGS = TYPOGRAPHY_HTML_TAGS;

export function normalizeTextElementTag(
	raw: string | undefined
): TypographyHtmlTag {
	const t = (raw ?? "p").trim().toLowerCase();
	return (TYPOGRAPHY_HTML_TAGS as readonly string[]).includes(t)
		? (t as TypographyHtmlTag)
		: "p";
}

/** Default heading level when missing or invalid (flow tags like `p` are coerced). */
export function normalizeHeadingElementTag(
	raw: string | undefined
): HeadingHtmlTag {
	const t = (raw ?? "h2").trim().toLowerCase();
	return (HEADING_HTML_TAGS as readonly string[]).includes(t)
		? (t as HeadingHtmlTag)
		: "h2";
}
